import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, exhaustMap, map, of, switchMap } from "rxjs";
import { KnowledgeApiService } from "../data-access/knowledge-api.service";
import { withSkeletonTiming } from "../../../shared/operators/skeleton-timing.operator";
import { KnowledgeActions } from "./knowledge.actions";

@Injectable()
export class KnowledgeEffects {
    private readonly actions$ = inject(Actions);
    private readonly api = inject(KnowledgeApiService);

    readonly load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(KnowledgeActions.pageOpened),
            switchMap(() =>
                this.api.load().pipe(
                    withSkeletonTiming(),
                    map((data) => KnowledgeActions.dataLoaded(data)),
                    catchError(() => of(KnowledgeActions.dataLoadFailed()))
                )
            )
        )
    );

    readonly save$ = createEffect(() =>
        this.actions$.pipe(
            ofType(KnowledgeActions.saveRequested),
            exhaustMap(({ section, draft, current }) =>
                this.api.save(section, draft, current).pipe(
                    map((item) => KnowledgeActions.saveSucceeded({ item })),
                    catchError(() => of(KnowledgeActions.mutationFailed()))
                )
            )
        )
    );

    readonly delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(KnowledgeActions.deleteRequested),
            exhaustMap(({ item }) =>
                this.api.delete(item).pipe(
                    map(() => KnowledgeActions.deleteSucceeded({ item })),
                    catchError(() => of(KnowledgeActions.mutationFailed()))
                )
            )
        )
    );

    readonly status$ = createEffect(() =>
        this.actions$.pipe(
            ofType(KnowledgeActions.statusChangeRequested),
            exhaustMap(({ item }) =>
                this.api
                    .setStatus(
                        item,
                        item.status === "pushed" ? "submitted" : "pushed"
                    )
                    .pipe(
                        map((updated) =>
                            KnowledgeActions.statusChangeSucceeded({
                                item: updated,
                            })
                        ),
                        catchError(() => of(KnowledgeActions.mutationFailed()))
                    )
            )
        )
    );
}
